import { useEffect, useState } from "react";
import {
  Pencil,
  Plus,
  Trash2,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import {
  api,
  formatApiError,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const empty = {
  name: "",
  email: "",
  password: "",
  role: "user",
};

export default function GestioneUtenti() {
  const { user, setUser } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const response = await api.get(
      "/admin/users"
    );

    setItems(
      Array.isArray(response.data)
        ? response.data
        : []
    );
  };

  useEffect(() => {
    if (user?.role === "admin") {
      load();
    }
  }, [user?.role]);

  if (user?.role !== "admin") {
    return null;
  }

  const nuovo = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const modifica = (item) => {
    setEditing(item);

    setForm({
      name: item.name || "",
      email: item.email || "",
      password: "",
      role: item.role || "user",
    });

    setOpen(true);
  };

  const salva = async (event) => {
    event.preventDefault();

    try {
      if (editing) {
        const response = await api.put(
          `/admin/users/${editing.id}`,
          form
        );

        if (editing.id === user.id) {
          setUser({
            ...user,
            name: response.data.name,
            email: response.data.email,
          });
        }

        toast.success("Utente aggiornato");
      } else {
        await api.post(
          "/admin/users",
          form
        );

        toast.success("Utente creato");
      }

      setOpen(false);
      setEditing(null);
      setForm(empty);
      load();
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        )
      );
    }
  };

  const elimina = async (item) => {
    const conferma = window.confirm(
      `Eliminare l'utente "${item.name}"?`
    );

    if (!conferma) return;

    try {
      await api.delete(
        `/admin/users/${item.id}`
      );

      toast.success("Utente eliminato");
      load();
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        )
      );
    }
  };

  return (
    <section className="mt-6 max-w-3xl rounded-xl border bg-card p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-2xl font-bold">
            <Users className="h-5 w-5" />
            Utenti CantierePro
          </h2>

          <p className="mt-1 text-sm text-muted-foreground">
            Solo Agostino può creare utenti e modificare le password.
          </p>
        </div>

        <Button
          type="button"
          onClick={nuovo}
        >
          <Plus className="mr-2 h-4 w-4" />
          Nuovo utente
        </Button>
      </div>

      <div className="divide-y rounded-lg border">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between gap-4 p-4"
          >
            <div>
              <p className="font-semibold">
                {item.name}
              </p>

              <p className="text-sm text-muted-foreground">
                {item.email} ·{" "}
                {item.role === "admin"
                  ? "Amministratore"
                  : item.role === "magazzino_alluminio"
                    ? "Solo Magazzino Alluminio"
                    : "Utente CantierePro"}
              </p>
            </div>

            <div className="whitespace-nowrap">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                title="Modifica utente e password"
                onClick={() =>
                  modifica(item)
                }
              >
                <Pencil className="h-4 w-4" />
              </Button>

              {item.role !== "admin" && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  title="Elimina utente"
                  onClick={() =>
                    elimina(item)
                  }
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog
        open={open}
        onOpenChange={setOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing
                ? "Modifica utente"
                : "Nuovo utente"}
            </DialogTitle>
          </DialogHeader>

          <form
            onSubmit={salva}
            className="space-y-4"
          >
            <div>
              <Label>Nome</Label>

              <Input
                value={form.name}
                onChange={(event) =>
                  setForm({
                    ...form,
                    name: event.target.value,
                  })
                }
                required
                placeholder="Nome utente"
              />
            </div>

            <div>
              <Label>Email</Label>

              <Input
                type="email"
                value={form.email}
                onChange={(event) =>
                  setForm({
                    ...form,
                    email: event.target.value,
                  })
                }
                required
                disabled={
                  editing?.role === "admin"
                }
              />
            </div>

            <div>
              <Label>
                {editing
                  ? "Nuova password"
                  : "Password"}
              </Label>

              <Input
                type="password"
                value={form.password}
                onChange={(event) =>
                  setForm({
                    ...form,
                    password:
                      event.target.value,
                  })
                }
                required={!editing}
                minLength={8}
                placeholder={
                  editing
                    ? "Lascia vuoto per non cambiarla"
                    : "Almeno 8 caratteri"
                }
              />
            </div>

            {editing?.role !== "admin" && (
              <div>
                <Label>Tipo di accesso</Label>
                <Select
                  value={form.role || "user"}
                  onValueChange={(value) => setForm({ ...form, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Utente CantierePro</SelectItem>
                    <SelectItem value="magazzino_alluminio">Solo Magazzino Alluminio</SelectItem>
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">
                  L’accesso “Solo Magazzino Alluminio” non consente di visualizzare cantieri, fatture o altre schede.
                </p>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  setOpen(false)
                }
              >
                Annulla
              </Button>

              <Button type="submit">
                Salva
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
